import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Loader2, Mic } from "lucide-react";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
interface VoiceButtonProps {
  onTranscript: (text: string) => void;
  disabled?: boolean;
  lang?: string;
  variant?: "outline" | "ghost";
  size?: "sm" | "default";
  className?: string;
  recordingClassName?: string;
  iconClassName?: string;
  recordingIconClassName?: string;
  "data-testid"?: string;
}

export function VoiceButton({
  onTranscript,
  disabled,
  lang = "en-US",
  variant = "outline",
  size = "sm",
  className,
  recordingClassName,
  iconClassName,
  recordingIconClassName,
  "data-testid": dataTestId,
}: VoiceButtonProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [isBrave, setIsBrave] = useState(false);
  const recognitionRef = useRef<any>(null);
  const committedTranscriptRef = useRef("");
  const { toast } = useToast();
  useEffect(() => {
    (navigator as any).brave?.isBrave().then((brave: boolean) => setIsBrave(brave)).catch(() => {});
  }, []);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch {}
        recognitionRef.current = null;
      }
    };
  }, []);

  const handleClick = useCallback(() => {
    if (!("webkitSpeechRecognition" in window) && !("SpeechRecognition" in window)) {
      toast({ title: "Voice input not supported", description: "Your browser doesn't support speech recognition.", variant: "destructive" });
      return;
    }

    if (isRecording && recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch {}
      return;
    }

    const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;

    let hadError = false;
    let lastFullTranscript = committedTranscriptRef.current;

    recognition.onstart = () => {
      setIsRecording(true);
      toast({ title: "Recording...", description: "Speak now. Click Stop when done." });
    };

    recognition.onresult = (event: any) => {
      let sessionTranscript = "";
      for (let i = 0; i < event.results.length; i++) {
        sessionTranscript += event.results[i][0].transcript;
      }
      const prefix = committedTranscriptRef.current;
      lastFullTranscript = prefix ? `${prefix} ${sessionTranscript}` : sessionTranscript;
      onTranscript(lastFullTranscript);
    };

    recognition.onerror = (event: any) => {
      hadError = true;
      setIsRecording(false);
      recognitionRef.current = null;
      if (event.error === "not-allowed" || event.error === "audio-capture") {
        toast({ title: "Microphone Error", description: "Microphone access was denied. Please allow microphone access and try again.", variant: "destructive" });
      } else if (event.error !== "no-speech" && event.error !== "aborted") {
        toast({ title: "Voice Error", description: event.error, variant: "destructive" });
        console.error("Speech recognition error:", event.error);
      }
    };

    recognition.onend = () => {
      setIsRecording(false);
      recognitionRef.current = null;
      if (!hadError) {
        committedTranscriptRef.current = lastFullTranscript;
        toast({ title: "Voice input complete", description: "Your text has been transcribed." });
      }
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch {
      setIsRecording(false);
      recognitionRef.current = null;
    }
  }, [isRecording, lang, onTranscript, toast]);

  if (isBrave) return null;

  return (
    <Button
      type="button"
      variant={variant}
      size={size}
      className={cn(className, isRecording && recordingClassName)}
      onClick={handleClick}
      disabled={disabled}
      data-testid={dataTestId}
    >
      {isRecording ? (
        <Loader2 className={cn("mr-2 h-4 w-4 animate-spin", recordingIconClassName)} />
      ) : (
        <Mic className={cn("mr-2 h-4 w-4", iconClassName)} />
      )}
      {isRecording ? "Stop Recording" : "Voice Input"}
    </Button>
  );
}
